import React from "react";
import { Button, Chip, HeartIcon, Logo, PawMark, ShieldIcon } from "./ui";
import { bruceSeed } from "./data";

/* -------------------------------------------------------------------------- */
/* Public Landing — marketing page shown before the app                       */
/* -------------------------------------------------------------------------- */

export default function Landing({ onEnterApp }) {
  return (
    <div
      className="min-h-screen overflow-x-hidden bg-[#FBFCFF] text-[#0B2D4D]"
      data-testid="landing-screen"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-slate-100 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 md:px-8">
          <Logo size="md" />
          <nav className="hidden items-center gap-7 text-sm font-bold text-slate-500 md:flex">
            <a href="#como-funciona" className="hover:text-[#0B2D4D]">Cómo funciona</a>
            <a href="#por-que" className="hover:text-[#0B2D4D]">Por qué PawCita</a>
            <a href="#seguridad" className="hover:text-[#0B2D4D]">Seguridad</a>
          </nav>
          <Button
            variant="primary"
            onClick={onEnterApp}
            testId="landing-header-cta"
            className="px-5 py-2.5 text-sm"
          >
            Entrar a la app
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-rose-200/40 blur-3xl" />
          <div className="absolute right-0 top-40 h-96 w-96 rounded-full bg-emerald-200/40 blur-3xl" />
        </div>
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 pb-16 pt-10 lg:grid-cols-[1.05fr_1fr] lg:gap-14 md:px-8 md:pb-24 md:pt-16">
          <div className="pc-rise">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-[#0B2D4D] shadow-sm">
              <span style={{ color: "#FF5C5C" }}>
                <PawMark size={14} />
              </span>
              Conecta, cuida y protege a tu perro
            </div>

            <h1 className="mt-5 font-display text-4xl font-black leading-[1.02] sm:text-5xl lg:text-6xl">
              Todo para la vida de tu perro,
              <br className="hidden sm:block" /> <span className="italic text-[#FF5C5C]">en una sola app</span>
            </h1>

            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-500 md:text-lg">
              Conecta con otros padres de perros, lleva su salud contigo, encuentra ayuda cuando la
              necesites y forma parte de una comunidad responsable.
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Button
                variant="primary"
                onClick={onEnterApp}
                testId="landing-hero-mvp-btn"
                className="px-7 py-4 text-base"
              >
                Crear mi cuenta
              </Button>
              <Button
                variant="outline"
                onClick={onEnterApp}
                testId="landing-hero-app-btn"
                className="px-7 py-4 text-base"
              >
                Entrar a la app
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-500">
              <span className="inline-flex items-center gap-1.5"><span className="text-emerald-500">✓</span> Sin redes sociales en el chat</span>
              <span className="inline-flex items-center gap-1.5"><span className="text-emerald-500">✓</span> Pasaporte de salud</span>
              <span className="inline-flex items-center gap-1.5"><span className="text-emerald-500">✓</span> Siempre contigo</span>
            </div>
          </div>

          {/* Right hero card */}
          <div className="pc-rise relative mx-auto w-full max-w-md lg:max-w-none">
            <div className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-emerald-50 via-white to-rose-50 p-3 shadow-[0_40px_80px_-40px_rgba(11,45,77,0.35)]">
              <div className="pc-paws absolute inset-0 opacity-60" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-[32px]">
                <img
                  src={bruceSeed.image}
                  alt="Bruce, un Jack Russell Terrier"
                  className="h-[460px] w-full object-cover md:h-[540px]"
                  style={{ objectPosition: "center top" }}
                  data-testid="landing-bruce-image"
                />
                <div
                  className="absolute right-5 top-5 inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 font-bold text-[#0B2D4D] shadow-lg"
                  data-testid="landing-bruce-badge"
                >
                  <span>Bruce está buscando match</span>
                  <span className="text-lg">🐶</span>
                </div>
                <div className="absolute bottom-5 left-5 right-5 rounded-3xl border border-white/60 bg-white/90 p-4 shadow-xl backdrop-blur">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-display text-xl font-black">
                        {bruceSeed.name} · {bruceSeed.breed}
                      </p>
                      <p className="text-sm text-slate-500">
                        {bruceSeed.age} años · {bruceSeed.sex} · {bruceSeed.location}
                      </p>
                    </div>
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl text-white" style={{ background: "#FF5C5C" }} aria-hidden="true">
                      <HeartIcon filled size={22} />
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Chip tone="mint" icon="💉">Vacunas al día</Chip>
                    <Chip tone="blue" icon="🩺">Sin enfermedades</Chip>
                    <Chip tone="coral">Sociable</Chip>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -left-4 top-10 hidden rotate-[-6deg] rounded-2xl bg-[#0B2D4D] px-4 py-3 text-white shadow-xl lg:block">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#67D5B5]">Verificado</p>
              <p className="font-display text-lg font-black">
                <span className="text-[#67D5B5]"><ShieldIcon size={16} className="inline-block align-[-2px]" /></span>{" "}
                Dueño responsable
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="relative bg-white py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="max-w-2xl">
            <p className="mb-2 text-xs font-black uppercase tracking-[0.2em] text-[#FF5C5C]">Cómo funciona</p>
            <h2 className="font-display text-3xl font-black leading-tight md:text-5xl">
              4 formas en que PawCita <span className="italic text-[#FF5C5C]">acompaña a tu perro</span>
            </h2>
            <p className="mt-3 text-slate-500 md:text-lg">
              Conecta, cuida su salud y encuentra ayuda cuando más la necesitas.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            <Step n="01" title="Crea su perfil" emoji="🐶" text="Registra sus datos, personalidad e información importante de salud." />
            <Step n="02" title="Conecta y comparte" emoji="🐾" text="Descubre otros perros, participa en actividades y crea conexiones responsables." />
            <Step n="03" title="Cuida su salud" emoji="🩺" text="Lleva su Pasaporte PawCita, cartilla digital e historial veterinario siempre contigo." />
            <Step n="04" title="Protégelo cuando importa" emoji="🆘" text="Activa PawCita SOS y encuentra atención veterinaria de emergencia cerca de ti." />
          </div>
        </div>
      </section>

      {/* Why PawCita */}
      <section id="por-que" className="relative py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid items-end gap-6 md:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-black uppercase tracking-[0.2em] text-[#FF5C5C]">Por qué PawCita</p>
              <h2 className="font-display text-3xl font-black leading-tight md:text-5xl">
                Una <span className="italic text-[#67D5B5]">comunidad</span> donde tu perro está primero
              </h2>
            </div>
            <p className="text-slate-500 md:text-lg">
              PawCita reúne conexión, salud y seguridad en un solo lugar para
              acompañarte durante toda la vida de tu perro.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            <Benefit emoji="🐾" title="Conexiones responsables" text="Conoce otros perros y padres de perros de forma segura." />
            <Benefit emoji="🩺" title="Pasaporte PawCita" text="Cartilla digital, vacunas e historial veterinario siempre disponibles." />
            <Benefit emoji="🆘" title="PawCita SOS" text="Activa una alerta y moviliza a la comunidad si tu perro se pierde." />
            <Benefit emoji="🚑" title="Emergencia veterinaria" text="Encuentra atención cercana, abierta o disponible 24 horas." />
            <Benefit emoji="💬" title="Chat seguro" text="Comunícate dentro de PawCita sin exponer tus datos personales." />
            <Benefit emoji="🌿" title="Comunidad y actividades" text="Participa en encuentros y actividades pensadas para perros y sus familias." />
          </div>
        </div>
      </section>

      {/* Safety section */}
      <section id="seguridad" className="relative bg-[#0B2D4D] py-16 text-white md:py-24">
        <div className="pc-paws absolute inset-0 opacity-20" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-black uppercase tracking-[0.2em] text-[#67D5B5]">Seguridad y bienestar animal</p>
              <h2 className="font-display text-3xl font-black leading-tight md:text-5xl">
                Promovemos <span className="italic text-[#FF8A8A]">bienestar y cuidado</span> responsable
              </h2>
              <p className="mt-4 text-emerald-50/85 md:text-lg">
                PawCita no es solo una herramienta — es un compromiso con el bienestar animal y la salud de tu perro.
              </p>
            </div>
            <ul className="space-y-3">
              <SafetyItem text="Recomendamos que los primeros encuentros sean en lugares públicos." />
              <SafetyItem text="Mantén al día la información de salud de tu perro." />
              <SafetyItem text="Consulta con tu veterinario antes de tomar decisiones de reproducción." />
              <SafetyItem text="PawCita te ayuda a conectar, cuidar y proteger a tu perro." />
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
          <h2 className="font-display text-3xl font-black leading-tight text-[#0B2D4D] md:text-5xl">
            ¿Listo para <span className="italic text-[#FF5C5C]">conectar, cuidar y proteger</span> a tu perro?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-500 md:text-lg">
            Únete gratis y lleva PawCita contigo en cada etapa de su vida.
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              variant="primary"
              onClick={onEnterApp}
              testId="landing-final-cta"
              className="px-8 py-4 text-base"
            >
              Crear mi cuenta
            </Button>
            <Button
              variant="outline"
              onClick={onEnterApp}
              testId="landing-final-app"
              className="px-8 py-4 text-base"
            >
              Entrar a la app
            </Button>
          </div>

          <div className="mt-5">
            <a
              href="/aliados"
              className="inline-flex items-center gap-2 text-sm font-black text-[#0B2D4D] transition hover:text-[#FF5C5C]"
              data-testid="landing-allies-portal-link"
            >
              🏪 ¿Eres un comercio aliado? Ingresa a tu panel →
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 bg-white py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 text-sm text-slate-500 md:flex-row md:px-8">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="hidden text-xs md:inline">© {new Date().getFullYear()} PawCita · Comunidad responsable para padres de perros</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs font-bold">
            <a href="#como-funciona" className="hover:text-[#0B2D4D]">Cómo funciona</a>
            <a href="#por-que" className="hover:text-[#0B2D4D]">Por qué PawCita</a>
            <a href="#seguridad" className="hover:text-[#0B2D4D]">Seguridad</a>
            <a
              href="/aliados"
              className="font-black text-[#0B2D4D] hover:text-[#FF5C5C]"
              data-testid="footer-allies-portal-link"
            >
              Portal de Aliados
            </a>
            <a
              href="/privacy"
              className="hover:text-[#0B2D4D]"
              data-testid="footer-privacy-link"
            >
              Privacidad
            </a>
            <a href="/delete-account" className="hover:text-[#0B2D4D]" data-testid="footer-delete-account-link">Eliminar cuenta</a>
            <a
              href="/terms"
              className="hover:text-[#0B2D4D]"
              data-testid="footer-terms-link"
            >
              Términos
            </a>
            <button
              onClick={onEnterApp}
              data-testid="landing-footer-cta"
              className="text-[#FF5C5C] hover:underline underline-offset-4"
            >
              Crear mi cuenta →
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Step({ n, title, emoji, text }) {
  return (
    <div className="group relative rounded-[28px] border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
      <div className="flex items-start justify-between">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-2xl">
          {emoji}
        </span>
        <span className="font-display text-3xl font-black text-slate-200">
          {n}
        </span>
      </div>
      <p className="mt-4 font-display text-xl font-black text-[#0B2D4D]">
        {title}
      </p>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">{text}</p>
    </div>
  );
}

function Benefit({ emoji, title, text }) {
  return (
    <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm transition hover:shadow-lg">
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-xl">
        {emoji}
      </span>
      <p className="mt-3 font-display text-lg font-black text-[#0B2D4D]">
        {title}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{text}</p>
    </div>
  );
}

function SafetyItem({ text }) {
  return (
    <li className="flex items-start gap-3 rounded-2xl bg-white/5 p-4 backdrop-blur">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#67D5B5] text-[#0B2D4D]">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </span>
      <p className="text-sm leading-relaxed text-emerald-50/90 md:text-base">
        {text}
      </p>
    </li>
  );
}
